import { ReactNode, MouseEvent } from "react";
import { useBoxStyles } from "../Box/useBoxStyles";
import styles from "./Button.module.scss";

interface Props {
  type?: "button" | "submit" | "reset";
  /** Indicates this button is the primary action for its context */
  primary?: boolean;
  disabled?: boolean;
  children: ReactNode;
  /**
   * Used to indicate button should be about as wide as it is high,
   * strips extra padding, used when only containing an icon
   */
  short?: boolean;
  variant?: "borderless" | "bordered";
  ariaLabel?: string;
  onClick?(event: MouseEvent<HTMLButtonElement>): void;
}

export function useButtonClasses(props: {
  short?: boolean;
  primary?: boolean;
  disabled?: boolean;
  font?: "accent" | "regular" | "special";
  variant?: "borderless" | "bordered";
}) {
  const { variant = "bordered" } = props;
  const boxStyles = useBoxStyles({
    component: "button",
    display: "block",
    borderRadius: "standard",
  });

  const classes: string[] = [styles["font-special"]];

  return [
    boxStyles,
    styles.namespace,
    styles.button,
    styles[variant],
    props.primary ? styles.primary : styles.standard,
    props.disabled && styles.disabled,
    props.short && styles.short,
    classes,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * A clickable button, which can be disabled to prevent user interaction
 */
export function Button(props: Props): React.ReactElement {
  const { type = "button" } = props;

  // NOTE: primary is used in the CSS but is not a valid button property
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { primary, ...restProps } = props;

  return (
    <button
      type={type}
      className={useButtonClasses({ ...props })}
      aria-label={props.ariaLabel}
      {...restProps}
    >
      {props.children}
    </button>
  );
}
